import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { Archive, Copy } from 'lucide-react';

import { LocalTime } from '~/components/common/LocalTime';
import { RadioCardGroup, Switch, TextField } from '~/components/form';
import { RecordPageShell } from '~/components/layout';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardSection,
  ConfirmModal,
  Disclosure,
  FormActions,
  Modal,
  Skeleton,
  StatusChip,
} from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { useUrlParams } from '~/hooks/useUrlParams';
import { isNotFound } from '~/lib/apiError';
import { queryErrorMessage } from '~/modules/query';
import {
  useArchiveFlagMutation,
  useCreateFlagMutation,
  useExplainFlagMutation,
  useGetFlagQuery,
  useUpdateFlagMutation,
} from '~/modules/query/featureFlags';

import {
  FLAG_KEY_RE,
  FLAG_STATUS_LABELS,
  flagStatus,
} from '~/components/Constants/featureFlagsConstants';
import AudienceEditor from '~/components/pages/feature-flags/AudienceEditor';
import {
  Select,
  cleanConfig,
  errorText,
  scheduleError,
  snapshot,
} from '~/components/pages/feature-flags/common';
import { EnvStatusField, ValueToggles } from '~/components/pages/feature-flags/commonUi';
import { useReference } from '~/components/pages/feature-flags/useReference';
import type { Reference } from '~/components/pages/feature-flags/common';
import type {
  EnvConfig,
  Explanation,
  Flag,
  FlagInput,
  Schedule,
} from '~/components/pages/feature-flags/types';
import { routes } from '~/app/routes';

interface Draft {
  key: string;
  products: string[];
  description: string;
  envs: Record<string, EnvConfig & Schedule>;
}

const blankEnv = (): EnvConfig & Schedule => ({
  enabled: false,
  startsAt: null,
  endsAt: null,
  defaultValue: false,
  rules: [],
});

// null = a new flag.
const toDraft = (reference: Reference, flag: Flag | null): Draft => ({
  key: flag?.key ?? '',
  products: flag?.products ?? [],
  description: flag?.description ?? '',
  envs: Object.fromEntries(
    reference.environments.map((e) => [e.key, flag?.envs[e.key] ?? blankEnv()]),
  ),
});

const toPayload = (draft: Draft): FlagInput => ({
  key: draft.key.trim(),
  products: draft.products,
  description: draft.description.trim(),
  envs: Object.fromEntries(Object.entries(draft.envs).map(([k, c]) => [k, cleanConfig(c)])),
});

const KEY_MESSAGE =
  'Use 2 to 63 lowercase letters, numbers and dashes, starting with a letter or number.';

const FORM_ID = 'flag-form';
const LIST = routes.featureFlagsSection('flags');

const reasonText = (result: Explanation, envName: string, defaultValue: boolean): string => {
  if (result.reason === 'disabled')
    return `The flag is disabled in ${envName}, outside its schedule, or the product is not assigned, so Off is served to everyone.`;
  if (result.reason === 'rule')
    return `Matched rule ${result.ruleIndex + 1}. Rules below it were not checked.`;
  return `No rule matched, so the default (${defaultValue ? 'On' : 'Off'}) was served.`;
};

export default function FlagPage() {
  const navigate = useNavigate();
  const url = useUrlParams();
  const { id } = useParams<{ id: string }>();
  const flagKey = id ?? '';
  const isNew = !id;
  const ref = useReference();
  const { environments, products, attributes } = ref.reference;

  const query = useGetFlagQuery(flagKey, { skip: isNew });
  const record = query.currentData?.flag;
  const audit = query.currentData?.audit ?? [];
  // `reference` is read at seed time only: a new list must not wipe unsaved edits (a save bumps `version`).
  const { draft, setDraft, patch, dirty, reset, markSaved } = useRecordDraft<Flag | null, Draft>(
    ref.loaded ? (isNew ? null : record) : undefined,
    { id: id ?? 'new', version: record?.version, toDraft: (flag) => toDraft(ref.reference, flag) },
  );
  const guard = useUnsavedGuard(dirty);

  const schema = useMemo(
    () =>
      z.object({
        key: isNew ? z.string().trim().regex(FLAG_KEY_RE, KEY_MESSAGE) : z.string(),
        products: z.array(z.string()).min(1, 'Choose at least one product.'),
        envs: z.custom<Draft['envs']>().superRefine((envs, ctx) => {
          const message = scheduleError(envs, environments);
          if (message) ctx.addIssue({ code: 'custom', message });
        }),
      }),
    [isNew, environments],
  );
  const check = useFormValidation(schema, draft, (field) =>
    field === 'products'
      ? `flag-product-${products[0]?.key}`
      : field === 'envs'
        ? 'flag-envs-error'
        : `flag-${field}`,
  );

  const [createFlag] = useCreateFlagMutation();
  const [updateFlag] = useUpdateFlagMutation();
  const [archiveFlag] = useArchiveFlagMutation();
  const [explainFlag] = useExplainFlagMutation();
  const createdKey = useRef<string | undefined>(undefined);
  const [archiving, setArchiving] = useState(false);
  const [copyFrom, setCopyFrom] = useState('');
  const [copyOpen, setCopyOpen] = useState(false);
  const [ctx, setCtx] = useState<Record<string, string>>({});
  const [explanation, setExplanation] = useState<Explanation | null>(null);
  const [explainFailed, setExplainFailed] = useState(false);

  const save = useRecordSave<Draft>({
    save: async (d) => {
      const payload = toPayload(d);
      if (isNew) createdKey.current = (await createFlag(payload).unwrap()).key;
      else if (record)
        await updateFlag({
          key: record.key,
          data: { ...payload, version: record.version },
        }).unwrap();
    },
    success: isNew ? 'Flag created' : 'Flag saved',
    failure: 'The flag could not be saved. Try again.',
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft || !check.submit()) return;
    if (!(await save.submit(draft))) return;
    if (!isNew) return markSaved(draft);
    guard.release();
    navigate(createdKey.current ? routes.featureFlagsRecord('flags', createdKey.current) : LIST, {
      replace: true,
    });
  };

  const archive = () => {
    if (!record) return;
    archiveFlag(record.key)
      .unwrap()
      .then(() => {
        toast.success('Flag archived');
        guard.release();
        navigate(LIST, { replace: true });
      })
      .catch((err) => toast.error(errorText(err, 'The flag could not be archived. Try again.')));
    setArchiving(false);
  };

  const requestedEnv = url.get('env');
  const active =
    environments.find((e) => e.key === requestedEnv)?.key ?? environments[0]?.key ?? '';
  const activeName = environments.find((e) => e.key === active)?.name ?? active;
  const current = draft?.envs[active] ?? blankEnv();
  const patchActive = (next: EnvConfig & Schedule) =>
    setDraft((d) => ({ ...d, envs: { ...d.envs, [active]: next } }));
  const otherEnvs = environments.filter((e) => e.key !== active);
  const copySource = otherEnvs.find((e) => e.key === copyFrom)?.key ?? otherEnvs[0]?.key ?? '';
  const ruleCount = (key: string) => draft?.envs[key]?.rules.length ?? 0;
  const copyRules = () => {
    const src = draft?.envs[copySource];
    if (src) patchActive({ ...current, rules: cleanConfig(src).rules });
    setCopyOpen(false);
  };

  // The live tester asks the service evaluator (the one copy of the rules); unsaved edits included.
  const currentSnapshot = snapshot(cleanConfig(current));
  const draftProducts = draft?.products;
  useEffect(() => {
    if (!active || !draft) return;
    let live = true;
    const timer = setTimeout(() => {
      explainFlag({
        flag: {
          key: draft.key || 'draft',
          products: draft.products.length ? draft.products : undefined,
          ...cleanConfig(current),
        },
        context: ctx,
      })
        .unwrap()
        .then((res) => {
          if (live) {
            setExplanation(res);
            setExplainFailed(false);
          }
        })
        .catch(() => {
          if (live) {
            setExplanation(null);
            setExplainFailed(true);
          }
        });
    }, 400);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [active, currentSnapshot, ctx, draftProducts, !!draft, explainFlag]);

  const set = <K extends keyof Draft>(name: K, value: Draft[K]) =>
    patch({ [name]: value } as Partial<Draft>);

  const title = isNew ? 'Create flag' : flagKey;
  const notFound = !isNew && isNotFound(query.error);
  const loadError = ref.loaded ? null : ref.error;
  const failed = (query.isError && !notFound) || !!loadError;

  const sections = draft && (
    <div className="min-w-0 space-y-6">
      <CardSection title="Details">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <TextField
            id="flag-key"
            testId="flag-key"
            label="Key"
            required
            code
            readOnly={!isNew}
            value={draft.key}
            onChange={(v) => set('key', v.toLowerCase())}
            error={check.errors.key}
            onBlur={() => check.touch('key')}
            helperText={
              isNew
                ? 'Lowercase letters, numbers and dashes. Used in code; it cannot change later.'
                : 'Used in code. It cannot change after creation.'
            }
          />
          <TextField
            id="flag-description"
            testId="flag-description"
            label="Description"
            value={draft.description}
            onChange={(v) => set('description', v)}
            placeholder="What this flag gates"
          />
        </div>
      </CardSection>

      <CardSection
        title="Products"
        description="The flag is only returned to apps in these products."
      >
        <ValueToggles
          idPrefix="flag-product"
          label="Products"
          hideLabel
          options={products.map((p) => ({ value: p.key, label: p.name }))}
          value={draft.products}
          onChange={(v) => set('products', v)}
          emptyText="Create a product before creating a flag."
        />
        {check.errors.products && (
          <p className="mt-2 text-xs text-error-600 dark:text-error-400">{check.errors.products}</p>
        )}
      </CardSection>

      <CardSection
        title="Environments"
        description="Each environment has its own status, schedule, rules and default. A disabled environment, or one outside its schedule, serves Off to everyone. Edits to every environment save together."
      >
        {environments.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            There are no environments yet. Add one on the Environments page before creating flags.
          </p>
        ) : (
          <div className="space-y-4">
            {check.errors.envs && (
              <p
                id="flag-envs-error"
                tabIndex={-1}
                role="alert"
                className="text-sm text-error-600 dark:text-error-400"
              >
                {check.errors.envs}
              </p>
            )}
            <RadioCardGroup
              name="flag-env"
              ariaLabel="Environment"
              value={active}
              onChange={(id) => url.set({ env: id })}
              options={environments.map((e) => {
                const c = draft.envs[e.key] ?? blankEnv();
                return {
                  value: e.key,
                  label: e.name,
                  description: FLAG_STATUS_LABELS[flagStatus(c)],
                  testId: `flag-env-${e.key}`,
                  end: (
                    <Switch
                      checked={c.enabled}
                      onChange={(enabled) =>
                        setDraft((d) => ({
                          ...d,
                          envs: { ...d.envs, [e.key]: { ...c, enabled } },
                        }))
                      }
                      ariaLabel={`Enabled in ${e.name}`}
                      testId={`flag-enabled-${e.key}`}
                    />
                  ),
                };
              })}
            />
            <div className="space-y-4">
              <EnvStatusField
                envKey={active}
                envName={activeName}
                idPrefix="flag"
                value={current}
                onChange={patchActive}
              />
              {current.enabled && (
                <AudienceEditor
                  idPrefix="flag-audience"
                  value={current}
                  onChange={patchActive}
                  attributes={attributes}
                  products={products}
                  decidingRule={explanation?.reason === 'rule' ? explanation.ruleIndex : null}
                  ruleActions={
                    otherEnvs.length > 0 &&
                    current.rules.length === 0 && (
                      <Button
                        variant="outline"
                        startIcon={<Copy className="size-4" aria-hidden="true" />}
                        onClick={() => setCopyOpen(true)}
                        testId="flag-copy"
                      >
                        Copy rules
                      </Button>
                    )
                  }
                />
              )}
            </div>
          </div>
        )}
      </CardSection>
    </div>
  );
  const formBlock = (
    <form id={FORM_ID} noValidate onSubmit={submit}>
      <Card>
        <CardBody className="space-y-6">
          {save.error && <Alert variant="error" message={save.error} />}
          {sections}
        </CardBody>
        <CardFooter>
          <FormActions
            form={FORM_ID}
            {...(isNew
              ? {
                  discardHref: LIST,
                  discardLabel: 'Cancel',
                  saveLabel: 'Create flag',
                  busyLabel: 'Creating…',
                }
              : {
                  onDiscard: () => {
                    reset();
                    check.reset();
                    save.clearError();
                  },
                  disabled: !dirty,
                  disabledReason: 'No changes to save',
                })}
            busy={save.busy}
            saveTestId="flag-save"
          />
        </CardFooter>
      </Card>
    </form>
  );

  return (
    <RecordPageShell
      title={title}
      breadcrumbs={[
        { label: 'Feature flags', href: routes.featureFlags },
        { label: 'Flags', href: LIST },
        { label: title },
      ]}
      actions={
        record
          ? [
              {
                label: 'Archive flag',
                kind: 'secondary',
                testId: 'flag-archive',
                startIcon: <Archive className="size-4" aria-hidden="true" />,
                onClick: () => setArchiving(true),
              },
            ]
          : undefined
      }
      loading={!draft}
      notFound={notFound}
      notFoundTitle="Flag not found"
      backTo={LIST}
      backLabel="Back to flags"
      error={
        failed ? { what: 'the flag', message: queryErrorMessage(query.error ?? loadError) } : null
      }
      onRetry={() => {
        if (query.isError) query.refetch();
        ref.refetch();
      }}
      guard={guard}
      testId="flag"
    >
      {draft && (
        <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
          <div className="min-w-0 space-y-6 xl:flex-1">
            {formBlock}

            {!isNew && (
              <Card>
                <CardBody>
                  <CardSection title="History">
                    {audit.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No changes recorded yet.
                      </p>
                    ) : (
                      <ol className="divide-y divide-gray-100 dark:divide-white/[0.04]">
                        {audit.map((a) => (
                          <li key={a.id} className="space-y-2 py-3 text-sm">
                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                              <LocalTime
                                value={a.createdAt}
                                className="text-gray-500 dark:text-gray-400"
                              />
                              <span className="font-medium text-gray-800 dark:text-white/90">
                                {a.actor}
                              </span>
                              <span className="text-gray-800 dark:text-white/90">{a.summary}</span>
                            </div>
                            {(a.before != null || a.after != null) && (
                              <Disclosure title="View diff">
                                <div className="grid grid-cols-1 gap-3 p-4 lg:grid-cols-2">
                                  {(
                                    [
                                      ['Before', a.before],
                                      ['After', a.after],
                                    ] as const
                                  ).map(([title, json]) => (
                                    <div key={title} className="min-w-0 space-y-1">
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {title}
                                      </p>
                                      <pre className="overflow-x-auto rounded-lg bg-gray-50 p-3 font-mono text-xs text-gray-800 dark:bg-gray-950 dark:text-white/90">
                                        {json == null ? '—' : JSON.stringify(json, null, 2)}
                                      </pre>
                                    </div>
                                  ))}
                                </div>
                              </Disclosure>
                            )}
                          </li>
                        ))}
                      </ol>
                    )}
                  </CardSection>
                </CardBody>
              </Card>
            )}
          </div>

          {environments.length > 0 && (
            <aside className="min-w-0 xl:sticky xl:top-0 xl:w-96 xl:shrink-0">
              <Card className="p-5">
                <CardSection
                  title={`Test a context in ${activeName}`}
                  description="Evaluates the rules as they are now, including unsaved edits."
                >
                  <div className="space-y-5">
                    <div className="space-y-3" aria-live="polite">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Result</p>
                      {explainFailed ? (
                        <p className="text-sm text-error-600 dark:text-error-400">
                          The result could not be checked. Edit the context to try again.
                        </p>
                      ) : explanation ? (
                        <>
                          <StatusChip
                            label={explanation.value ? 'On' : 'Off'}
                            tone={explanation.value ? 'success' : 'neutral'}
                            dot
                            testId="flag-test-result"
                          />
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {reasonText(explanation, activeName, current.defaultValue)}
                          </p>
                        </>
                      ) : (
                        <Skeleton className="w-40" />
                      )}
                    </div>
                    <div className="space-y-4">
                      {attributes.map((a) =>
                        a.key === 'product' ? (
                          <Select
                            key={a.key}
                            id="flag-ctx-product"
                            label="product"
                            value={ctx.product ?? ''}
                            options={products.map((p) => ({ value: p.key, label: p.name }))}
                            placeholder="Choose product"
                            placeholderSelectable
                            onChange={(v) => setCtx((s) => ({ ...s, product: v }))}
                          />
                        ) : (
                          <TextField
                            key={a.key}
                            id={`flag-ctx-${a.key}`}
                            testId={`flag-ctx-${a.key}`}
                            label={a.key}
                            code
                            helperText={a.notes ?? undefined}
                            value={ctx[a.key] ?? ''}
                            onChange={(v) => setCtx((s) => ({ ...s, [a.key]: v }))}
                          />
                        ),
                      )}
                      {attributes.length === 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Add attributes on the Attributes page to test a context.
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Context sent by the SDK
                      </p>
                      <pre className="overflow-x-auto rounded-lg bg-gray-50 p-3 font-mono text-xs text-gray-800 dark:bg-gray-950 dark:text-white/90">
                        {JSON.stringify(ctx, null, 2)}
                      </pre>
                    </div>
                  </div>
                </CardSection>
              </Card>
            </aside>
          )}
        </div>
      )}

      <Modal
        isOpen={copyOpen}
        onClose={() => setCopyOpen(false)}
        title="Copy rules"
        description={`Copy another environment's rules into ${activeName}. Nothing is saved until you click Save changes.`}
        testId="flag-copy-modal"
        footer={
          <FormActions
            discardLabel="Cancel"
            onDiscard={() => setCopyOpen(false)}
            onSave={copyRules}
            saveLabel="Copy rules"
            disabled={ruleCount(copySource) === 0}
            disabledReason="That environment has no rules"
            saveTestId="flag-copy-confirm"
          />
        }
      >
        <RadioCardGroup
          name="flag-copy-from"
          ariaLabel="Copy rules from"
          value={copySource}
          onChange={setCopyFrom}
          options={otherEnvs.map((e) => ({
            value: e.key,
            label: e.name,
            description:
              ruleCount(e.key) === 1
                ? '1 rule'
                : ruleCount(e.key)
                  ? `${ruleCount(e.key)} rules`
                  : 'No rules',
            testId: `flag-copy-from-${e.key}`,
          }))}
        />
      </Modal>

      <ConfirmModal
        isOpen={archiving}
        title="Archive flag"
        message={`Archive '${flagKey}'? The flag stops being returned to your apps. Each check falls back to its in-code default.`}
        confirmLabel="Archive flag"
        testId="flag-archive-confirm"
        onClose={() => setArchiving(false)}
        onConfirm={archive}
      />
    </RecordPageShell>
  );
}
